import { useState } from 'react';
import { postIdea, type Idea } from '@/hooks/useIdeas';
import { getSavedName, saveName } from '@/lib/visitor';
import { trackEvent } from '@/lib/analytics';
import ShareButtons from './ShareButtons';

const MAX = 1000;

interface IdeaComposerProps {
  id?: string;
  onPosted?: (idea: Idea) => void;
}

const IdeaComposer = ({ id, onPosted }: IdeaComposerProps) => {
  const [body, setBody] = useState('');
  const [name, setName] = useState(getSavedName());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [posted, setPosted] = useState<Idea | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = body.trim();
    if (trimmed.length < 3) {
      setError('Give us at least a few words.');
      return;
    }
    if (trimmed.length > MAX) {
      setError(`Keep it under ${MAX} characters.`);
      return;
    }
    setError(null);
    setLoading(true);
    try {
      saveName(name);
      const idea = await postIdea(trimmed, null, name.trim() || null);
      setPosted(idea);
      setBody('');
      trackEvent('idea_posted');
      onPosted?.(idea);
    } catch {
      setError('Something glitched. Try again?');
    } finally {
      setLoading(false);
    }
  };

  if (posted) {
    return (
      <div id={id} className="rounded-lg border border-primary/40 bg-card/60 p-6 text-center">
        <p className="font-bebas text-3xl text-foreground">Your idea is live 🚀</p>
        <p className="font-mono text-sm text-muted-foreground mt-2 mb-4">
          Now go make it spread. Share it anywhere.
        </p>
        <p className="font-playfair italic text-lg text-foreground/90 mb-4">"{posted.body}"</p>
        <div className="flex justify-center">
          <ShareButtons ideaId={posted.id} text={posted.body} compact />
        </div>
        <button
          onClick={() => setPosted(null)}
          className="mt-5 font-mono text-xs text-primary hover:underline"
        >
          Post another idea
        </button>
      </div>
    );
  }

  return (
    <form id={id} onSubmit={submit} className="rounded-lg border border-border bg-card/60 p-5">
      <label htmlFor="idea-body" className="font-bebas text-2xl text-foreground tracking-wide">
        What's your idea?
      </label>
      <textarea
        id="idea-body"
        value={body}
        onChange={e => { setBody(e.target.value); setError(null); }}
        onFocus={() => trackEvent('composer_focused')}
        rows={4}
        maxLength={MAX}
        placeholder="A doorbell that rings only for people you like..."
        className="mt-3 w-full rounded bg-background/60 border border-border px-4 py-3 font-mono text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors resize-y"
      />
      <div className="mt-3 flex flex-col sm:flex-row gap-3">
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          maxLength={40}
          placeholder="Your name (optional)"
          aria-label="Your name, optional"
          className="flex-1 h-11 px-4 rounded bg-background/60 border border-border font-mono text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
        />
        <button
          type="submit"
          disabled={loading}
          className="h-11 px-8 rounded bg-primary text-primary-foreground font-bebas text-lg tracking-wide hover:bg-primary/90 disabled:opacity-50 transition-all whitespace-nowrap"
        >
          {loading ? 'Posting...' : 'Post It 🚀'}
        </button>
      </div>
      <div className="mt-2 flex items-center justify-between font-mono text-xs text-muted-foreground">
        <span>{error ?? 'Anyone can post. No sign-up needed.'}</span>
        <span>{body.length}/{MAX}</span>
      </div>
    </form>
  );
};

export default IdeaComposer;
